import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    handleGetOrganizationsShortDetailsData,
    setOrderByDirection,
    setOrderByProperty,
    resetState,
    resetScroll,
    getModalSwitch,
} from '@app/store/actions';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import {
    getMenuItemById,
    defaultOrgId,
    infiniteScrollItemsFetchLimit,
    genericErrorToast,
    genericErrorString,
    interpolateTranslation,
} from '@app/helpers';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { IOrganizationShortDetails, ITableHead, IFabMenu, IListPageBarEnabled, IActiveFilter, FilterEnum, OrderType, Order, Intercom } from '@app/models';
import { hasOrgPermission, hasDefaultOrgAdminPermission, isDefaultOrgUser } from '@app/helpers/permissions';
import { ManageSystem } from '@app/store/constants';
import { basicService, organizationsService } from '@app/services';
import { FabMenu, ModalComponent, IModalData, PasslyTable } from '@app/components';
import { confirm } from '@app/components/Modal/confirmModal';
import AddOrganizationDialog from '@app/pages/directoryManager/dialogs/addOrganization.dialog';
import { TableCell } from '@material-ui/core';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import { ReportProblem } from '@material-ui/icons';

interface IOrgsTableData {
    name: string;
    homeRealm: string;
    provisioningState: string;
    darkWebCompromiseCount: number;
    defaultPolicyDisplayName: string;
    id: string;
    checked: boolean;
    cantDelete: boolean;
    cantEdit: boolean;
}

enum TableHeadEnum {
    name = 'name',
    defaultPolicyDisplayName = 'defaultPolicyDisplayName',
    homeRealm = 'homeRealm',
    provisioningState = 'provisioningState',
    DarkWebCompromiseCount = 'DarkWebCompromiseCount'
}

const OrganizationsPage = () => {
    // -------------------------------------------
    // ------------------ DATA -------------------
    // -------------------------------------------
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const orgs = useSelector((state: any) => state.orgs.orgs);
    const orgsCount = useSelector((state: any) => state.orgs.count);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const homeRealmSuffix: string = useSelector((state: any) => state.basic.shellConfig.configs.homeRealmSuffix);
    const intercom : Intercom = useSelector((state: any) => state.basic.shellConfig.intercom);

    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Organizations);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('name');
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [rows, setRows] = useState<IOrgsTableData[]>([]);
    const [organizationLimitText, setOrganizationLimitText] = useState('');
    const [overallOrgsCount, setOverallOrgsCount] = useState<number>(0);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: false
    };

    const editRow = (e: any) => {
        basicService.redirect('/directory/orgs/details/' + e.currentTarget.dataset.id);
    };

    const getComplexCell = (row: any) => {
        if (row.darkWebCompromiseCount === null || row.darkWebCompromiseCount === -1) {
            return <TableCell>&nbsp;</TableCell>;
        }

        if (row.darkWebCompromiseCount === 0) {
            return (
                <TableCell className='organization__icon'>
                    <ThumbUpIcon fontSize='small' htmlColor='green' onClick={() => showDarkWebMessage(row)} />
                </TableCell>
            );
        }

        if (row.darkWebCompromiseCount > 0) {
            return (
                <TableCell className='organization__icon'>
                    <ReportProblem color='error' onClick={() => showDarkWebMessage(row)} />
                </TableCell>
            );
        }
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.name,
            label: 'ORG_NAME',
            type: 'link',
            filterType: FilterEnum.contains,
            defaultSearchBy: TableHeadEnum.name,
            onClick: editRow
        },
        {
            id: TableHeadEnum.defaultPolicyDisplayName,
            label: 'ORG_POLICY',
        },
        {
            id: TableHeadEnum.homeRealm,
            label: 'ORG_HOMEREALM',
        },
        {
            id: TableHeadEnum.provisioningState,
            label: 'ORG_STATE',
            width: '140px',
            type: 'status',
        },
    ];

    if (intercom.dwidEnabled) {
        headCells.push(
            {
                id: TableHeadEnum.DarkWebCompromiseCount,
                label: 'DARK_WEB_COMPROMISE_COUNT',
                type: 'getComplexCell',
                width: '140px',
                getComplexCell: getComplexCell
            });
    }

    const menu: IFabMenu = {
        name: 'NEW_ORG',
        actions: [
            {
                name: 'NEW_ORG',
                method: () => addNewOrg(),
                iconElement: <i className='icon icon-organizations' />
            }
        ]
    };

    // -------------------------------------------
    // ----------------- EFFECTS -----------------
    // -------------------------------------------
    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        if (!loadingData && orgs) {
            let newRows = orgs.map((org: IOrganizationShortDetails) => {
                const data: IOrgsTableData = {
                    name: org.Name,
                    homeRealm: `https://${org.HomeRealm}.${homeRealmSuffix}`,
                    provisioningState: org.ProvisioningState,
                    darkWebCompromiseCount: org.DarkWebCompromiseCount,
                    defaultPolicyDisplayName: org.DefaultPolicyDisplayName,
                    id: org.Id,
                    checked: false,
                    cantDelete: (org.Id === defaultOrgId || !hasOrgPermission([ManageSystem])) || !hasDefaultOrgAdminPermission(),
                    cantEdit: !hasOrgPermission([ManageSystem]),
                };
                return data;
            });

            if (orgs.length === 0) {
                setRows([]);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    }, [loadingData, orgs]);

    useEffect(() => {
        if (overallOrgsCount === 0 && orgsCount !== 0) {
            // allows the total org count and org limit to be only be set once on first load of page
            setOverallOrgsCount(orgsCount);
            organizationsService.getOrgLimit(genericErrorString, handleSuccessOrgLimit.bind(null, orgsCount));
        }
    }, [orgsCount]);

    // -------------------------------------------
    // --------------- FUNCTIONS -----------------
    // -------------------------------------------
    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (loadingData) {
            return;
        }

        dispatch(handleGetOrganizationsShortDetailsData(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, isReset, activeFilters));
    };

    const addNewOrg = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_ORG'),
            modalSwitcher: modalSwitchState,
            form: <AddOrganizationDialog handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            setRows([]);
            dispatch(resetScroll());
        }
    };

    const deleteRow = (e: any) => {
        const chosenId: string = e.currentTarget.dataset.id;

        confirm(translation('DELETE_ORG_BODY'), translation('DELETE'), translation('CANCEL'), translation('DELETE_ORG_HEADER'), false, {
            isDeleteAction: true
        }).then(
            () => {
                deleteOrg(chosenId);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deleteOrg = (id: string) => {
        const rowSelected: IOrgsTableData[] = rows.filter((item) => item.id === id);

        if (rowSelected && rowSelected.length === 1) {
            organizationsService.deleteOrgs(id, genericErrorToast, handleDeleteSuccess.bind(null, id));
        }
    };

    const handleDeleteSuccess = (id: string) => {
        if (id === organizationFilter) {
            // If we're deleting the child org we're currently filtered to
            // then reset the filter to the default org
            basicService.setOrgFilter(defaultOrgId);
        }

        // too much changes on the site with org delete, do a browser reload
        window.location.reload();
    };

    const handleSuccessOrgLimit = (actualOrgsCount: number, response: any) => {
        const orgLimit: number = response.data.value;
        let limitText: string = orgLimit.toString();

        if (orgLimit === 0) {
            limitText = translation('UNLIMITED');
        }

        const args: string[] = [actualOrgsCount.toString(), limitText];

        const orgLimitText = interpolateTranslation(translation('ORG_LICENSE_LIMIT_MSG'), args);

        setOrganizationLimitText(orgLimitText);
    };

    const showDarkWebMessage = (row: IOrgsTableData) => {
        let body: string = '';

        if (row.darkWebCompromiseCount > 0) {
            const args: string[] = [row.name, row.darkWebCompromiseCount.toString()];
            body = interpolateTranslation(translation('DARK_WEB_COMPROMISE_TEXT'), args);
        } else {
            const args: string[] = [row.name];
            body = interpolateTranslation(translation('DARK_WEB_NO_COMPROMISE_TEXT'), args);
        }

        body += ` <a href="https://www.idagent.com/passlydarkweb" target="_blank">${translation('HERE')}</a>`;

        confirm(body, translation('CLOSE_BUTTON'), undefined, row.name, true).then(
            () => {
                // do nothing on success
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    return (
        <>
            <ModalComponent {...modalData} />

            {hasDefaultOrgAdminPermission() && <FabMenu menu={menu} />}

            {!hasOrgPermission([ManageSystem]) ? (
                <div className='table--empty'>{translation('NO_ORG_ORG_PERMISSIONS')}</div>
            ) : (
                <>
                    {isDefaultOrgUser() && <div className='rowcontainer licenseSummary'>{!loadingData && <div>{organizationLimitText}</div>}</div>}
                    <PasslyTable
                        rows={rows}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={orgsCount}
                        onFetch={fetchMoreData}
                        withCheckboxes={false}
                        emptyListHelper={translation('EMPTY_ORG_LIST')}
                        loadingData={loadingData}
                        editRow={editRow}
                        deleteRow={deleteRow}
                    />
                </>
            )}
        </>
    );
};

export default OrganizationsPage;
